import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaTripadvisor } from 'react-icons/fa';

import { navBrands, PIZZA_INN_LABEL } from '../../config/brands';
import { useApp } from '../../context/AppContext';
import '../../styles/Footer.css';

/**
 * The back page.
 *
 * This was a dark-green band (#1a1e1a, a colour that exists nowhere in the
 * design's palette) carrying gold icons, filled circular social buttons that
 * lifted and cast a shadow on hover, and a `color: ... !important` rule that
 * forced every descendant to one cream. It is now ink on paper, turned around:
 * the closing band is ink because the page has ended, and the words are the
 * restaurant's own.
 */
const Footer = () => {
  const { t } = useTranslation();
  const { restaurantInfo } = useApp();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/menu', label: PIZZA_INN_LABEL },
    ...navBrands.map((b) => ({
      path: b.route,
      label: b.nav.key ? t(b.nav.key, b.nav.fallback) : b.nav.fallback,
    })),
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/reservations', label: t('nav.reservations') },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__brand">
          <p className="footer__wordmark">Pizza Inn</p>
          <p className="footer__est">est. 1995 · Baku</p>
          <p className="footer__tagline">
            {t('footer.tagline', 'The classics of Azerbaijani cuisine and the finest flavours of world cuisine.')}
          </p>
          <div className="footer__social">
            <a
              href={restaurantInfo.socialMedia.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FaFacebookF aria-hidden="true" />
            </a>
            <a
              href={restaurantInfo.socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram aria-hidden="true" />
            </a>
            <a
              href={restaurantInfo.socialMedia.tripadvisor}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Tripadvisor"
            >
              <FaTripadvisor aria-hidden="true" />
            </a>
          </div>
        </div>

        <nav className="footer__col" aria-label={t('footer.quickLinks', 'Quick links')}>
          <p className="footer__label">{t('footer.quickLinks', 'Quick links')}</p>
          <ul>
            {quickLinks.map((link) => (
              <li key={link.path}>
                <Link to={link.path}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="footer__col">
          <p className="footer__label">{t('footer.contact')}</p>
          <ul>
            <li>
              <a href={restaurantInfo.mapsUrl} target="_blank" rel="noopener noreferrer">
                {t('footer.addressText')}
              </a>
            </li>
            <li>
              <a href={`tel:${String(restaurantInfo.phone).replace(/\s/g, '')}`}>
                {restaurantInfo.phone}
              </a>
            </li>
            {restaurantInfo.email && (
              <li>
                <a href={`mailto:${restaurantInfo.email}`}>{restaurantInfo.email}</a>
              </li>
            )}
          </ul>
        </div>

        <div className="footer__col">
          <p className="footer__label">{t('footer.hours')}</p>
          <ul>
            <li>{t('footer.everyday', 'Every day')}</li>
            <li>{restaurantInfo.workingHours.daily}</li>
          </ul>
        </div>
      </div>

      <div className="footer__bottom">
        <p>© {currentYear} {restaurantInfo.name}</p>
      </div>
    </footer>
  );
};

export default Footer;
